import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { useAnimateOnScroll } from '../hooks';

const jobs = [
  {
    id: 'gov-dc-prod',
    role: 'DevSecOps & AI Infra Engineer',
    company: 'Government Data Center (UAE) • On-Premise',
    date: 'Present',
    status: 'ACTIVE_NODE',
    logs: [
      'Architected GPU-enabled AI infrastructure using NVIDIA Base Command Manager (BCM) and Run:AI within air-gapped environments.',
      'Managed VMware Cloud Foundation and Tanzu K8s clusters from bootstrap through production, handling nested K8s architectures for multi-tenant isolation.',
      'Enforced GitOps compliance via ArgoCD and GitLab, revoking direct Kubernetes access for developers to ensure governance.',
      'Resolved complex GPU lifecycle operations: driver upgrades, NVIDIA Container Toolkit config, GPU Operator deployment, and Device Plugin integration.',
      'Deployed centralized, dark-network observability stacks utilizing Prometheus, Grafana, ELK, and OpenTelemetry.'
    ]
  },
  {
    id: '2cloud-prod',
    role: 'DevSecOps Engineer',
    company: '2CLOUD FZE (UAE)',
    date: 'June 2019 - Dec 2025',
    status: 'ARCHIVED_NODE',
    logs: [
      'Architected full-stack infrastructure across AWS, Azure, GCP, VMware, and OpenShift supporting 20,000+ concurrent users for eCommerce and FinTech systems.',
      'Executed 30+ zero-downtime cloud migrations from legacy on-premise hosts/Linode to modern containerized environments.',
      'Troubleshot and resolved complex Kubernetes networking and stability issues, including deep Calico CNI debugging and ingress optimizations.',
      'Created one-click provisioning IaC stacks (Terraform/CloudFormation) for Magento and Laravel, cutting deployment times significantly.',
      'Implemented dynamic edge caching (Cloudflare/CloudFront) and built secure platforms utilizing OIDC, SSO, Private CA, and VPN-free access models.',
      'Designed Hyperledger Fabric blockchain infrastructure for land registry operations with smart contract automation via n8n.'
    ]
  }
];

const JobItem = ({ job, inView, idx }: { job: any, inView: boolean, idx: number }) => {
  const [isExpanded, setIsExpanded] = useState(false);

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={inView ? { opacity: 1, y: 0 } : {}}
      transition={{ duration: 0.5, delay: idx * 0.2 }}
      className="border-l-2 border-zinc-800 pl-5 sm:pl-8 relative group"
    >
      <div className="absolute -left-[9px] top-2 w-4 h-4 bg-[#050505] border-2 border-zinc-700 group-hover:border-white group-hover:bg-white transition-all rounded-full" />

      <div className="flex flex-col lg:flex-row lg:items-start justify-between gap-4">
        <div>
          <h3 className="text-[14px] sm:text-[18px] md:text-[24px] font-mono font-medium text-white uppercase tracking-tight mb-2">
            {job.role}
          </h3>
          <div className="flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-4 mt-2">
            <p className="font-mono text-sm sm:text-base text-zinc-400">{job.company}</p>
            <span className="hidden sm:block text-zinc-700">|</span>
            <p className="font-mono text-sm text-zinc-300 bg-zinc-900/50 px-2 py-0.5 rounded w-fit">
              {job.date}
            </p>
          </div>
        </div>

        <div className="w-fit">
          <span className={`font-mono text-[10px] sm:text-xs px-3 py-1.5 tracking-widest uppercase flex items-center gap-2 ${job.status === 'ACTIVE_NODE' ? 'bg-white text-black font-bold' : 'border border-zinc-700 text-zinc-400'}`}>
            {job.status === 'ACTIVE_NODE' && (
              <span className="relative flex h-2 w-2">
                <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-black opacity-60"></span>
                <span className="relative inline-flex rounded-full h-2 w-2 bg-black"></span>
              </span>
            )}
            STATUS: {job.status}
          </span>
        </div>
      </div>

      <button
        onClick={() => setIsExpanded(!isExpanded)}
        className="md:hidden mt-4 w-full bg-zinc-900/30 border border-zinc-800 text-zinc-400 hover:text-white flex justify-between items-center px-4 py-3 font-mono text-[10px] tracking-widest uppercase"
      >
        <span>{isExpanded ? 'View Less' : 'View More'}</span>
        <span>{isExpanded ? '[-]' : '[+]'}</span>
      </button>

      <div className={`${isExpanded ? 'block' : 'hidden'} md:block mt-4 md:mt-6 space-y-4 bg-[#0a0a0a] border border-zinc-800/80 p-4 sm:p-6 font-mono text-xs sm:text-sm text-zinc-300 transition-colors group-hover:border-zinc-700`}>
        {job.logs.map((log: string, i: number) => (
          <div key={i} className="flex items-start gap-3 sm:gap-4">
            <span className="text-zinc-600 select-none mt-0.5">{`[${String(i + 1).padStart(2, '0')}]`}</span>
            <span className="leading-relaxed text-zinc-300">{log}</span>
          </div>
        ))}
      </div>
    </motion.div>
  );
};

const Experience: React.FC = () => {
  const { ref, inView } = useAnimateOnScroll();

  return (
    <section id="experience" className="py-16 sm:py-24 bg-[#050505] px-4 sm:px-8 md:px-12 border-t border-zinc-900 overflow-hidden">
      <div className="max-w-6xl mx-auto" ref={ref}>
        <div className="mb-12 sm:mb-16">
          <h2 className="text-2xl sm:text-3xl md:text-4xl font-mono font-medium text-white uppercase tracking-tight mb-2">
            ~/Professional Experience
          </h2>
          <div className="text-zinc-500 font-mono text-xs sm:text-sm tracking-widest uppercase">
            02 // Professional Background
          </div>
        </div>

        <div className="space-y-12 sm:space-y-16">
          {jobs.map((job, idx) => (
            <JobItem key={job.id} job={job} inView={inView} idx={idx} />
          ))}
        </div>
      </div>
    </section>
  );
};

export default Experience;